import React, { useRef, useState } from "react";
import { ReactComponent as SampleUploadIcon } from "../../../Assets/Icons/SampleUploadIcon.svg";
import { ReactComponent as UploadCSVIcon } from "../../../Assets/Icons/UploadCSVIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../Assets/Icons/downloadIcon.svg";
import CloseIcon from "../../../Assets/Icons/CloseIconSvg.svg";

const AddCodesModal = ({ onClose, onAddCodes, offerId, merchantId }) => {
  const fileInputRef = useRef();
  const [codes, setCodes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  // Validation functions
  const validateCode = (code) => {
    const errors = [];
    
    // Only check if code is valid format and length
    if (code.length < 4 || code.length > 20 || !/^[A-Z0-9]+$/.test(code)) {
      errors.push(`"${code}" invalid Code`);
    }
    
    return errors;
  };

  const processCodes = (codesArray) => {
    const processedCodes = codesArray
      .map(code => code.trim().toUpperCase()) // Auto-capitalize and trim
      .filter(code => code.length > 0); // Remove empty codes
    
    // Remove duplicates
    const uniqueCodes = [...new Set(processedCodes)];
    
    return {
      codes: uniqueCodes,
      duplicates: processedCodes.length - uniqueCodes.length
    };
  };

  const validateCodes = (codesArray) => {
    const errors = [];
    const { codes: processedCodes, duplicates } = processCodes(codesArray);
    
    // Check for duplicates
    if (duplicates > 0) {
      errors.push(`${duplicates} duplicate code(s) were removed`);
    }
    
    // Validate each code
    processedCodes.forEach(code => {
      const codeErrors = validateCode(code);
      errors.push(...codeErrors);
    });
    
    return {
      validCodes: processedCodes,
      errors: errors
    };
  };

  const handleAddCodes = async () => {
    if (!codes.trim()) {
      setError("Please enter at least one code");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      setValidationErrors([]);
      
      // Parse codes from comma-separated string
      const codesArray = codes.split(',').map(code => code.trim()).filter(code => code.length > 0);
      
      console.log("AddCodesModal - Parsed codes:", codesArray);
      
      if (codesArray.length === 0) {
        setError("Please enter valid codes");
        return;
      }

      // Validate and process codes
      const { validCodes, errors } = validateCodes(codesArray);
      
      if (errors.length > 0) {
        setValidationErrors(errors);
        // Still proceed if there are only duplicate warnings
        const hasSeriousErrors = errors.some(error => 
          !error.includes('duplicate') && !error.includes('removed')
        );
        if (hasSeriousErrors) {
          return;
        }
      }

      if (validCodes.length === 0) {
        setError("No valid codes found after validation");
        return;
      }

      console.log("AddCodesModal - Validated codes:", validCodes);
      
      // Call the parent handler with the validated codes array
      await onAddCodes(validCodes);
      
      // Clear the form
      setCodes("");
      setValidationErrors([]);
      
    } catch (error) {
      console.error("AddCodesModal - Error adding codes:", error);
      setError("Failed to add codes. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError("Please upload a valid CSV file");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      setValidationErrors([]);
      
      const text = await file.text();
      console.log("CSV file content:", text);
      
      // Handle different line endings (Windows: \r\n, Unix: \n, Mac: \r)
      const lines = text.split(/\r\n|\n|\r/).map(line => line.trim()).filter(line => line);
      console.log("CSV parsed lines:", lines);
      
      // Skip header if it exists (check if first line contains 'code' or similar)
      const startIndex = lines[0] && lines[0].toLowerCase().includes('code') ? 1 : 0;
      console.log("Header detected, starting from index:", startIndex);
      
      // Handle CSV with commas (multiple codes per line) or single code per line
      let csvCodes = [];
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i];
        if (line.includes(',')) {
          // Line contains comma-separated codes
          const codesInLine = line.split(',').map(code => code.trim()).filter(code => code.length > 0);
          csvCodes.push(...codesInLine);
        } else if (line.length > 0) {
          // Single code per line
          csvCodes.push(line);
        }
      }
      
      console.log("Extracted CSV codes:", csvCodes);
      
      if (csvCodes.length === 0) {
        setError("No valid codes found in the CSV file");
        return;
      }

      // Process and validate CSV codes
      const { validCodes, errors } = validateCodes(csvCodes);
      console.log("Validation result - validCodes:", validCodes);
      console.log("Validation result - errors:", errors);
      
      if (errors.length > 0) {
        setValidationErrors(errors);
        // Still proceed if there are only duplicate warnings
        const hasSeriousErrors = errors.some(error => 
          !error.includes('duplicate') && !error.includes('removed')
        );
        if (hasSeriousErrors) {
          console.log("Serious errors found, stopping:", errors);
          return;
        }
      }

      if (validCodes.length === 0) {
        setError("No valid codes found in the CSV file after validation");
        return;
      }

      console.log("Valid codes found:", validCodes.length, validCodes);

      // Add CSV codes to existing codes
      const existingCodes = codes.trim() ? codes.split(',').map(c => c.trim()) : [];
      const allCodes = [...existingCodes, ...validCodes];
      
      console.log("Existing codes:", existingCodes);
      console.log("All codes combined:", allCodes);
      
      // Process all codes together to remove duplicates
      const { codes: finalCodes } = processCodes(allCodes);
      console.log("Final processed codes:", finalCodes);
      
      setCodes(finalCodes.join(', '));
      
      // Clear any previous validation errors since CSV was processed successfully
      setValidationErrors([]);
      
    } catch (error) {
      console.error("Error reading CSV file:", error);
      setError("Failed to read CSV file. Please try again.");
    } finally {
      setIsLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSampleFileDownload = () => {
    try {
      // Create a sample CSV content with proper format and examples
      // Show both formats: single code per line and comma-separated codes
      const csvContent = `code
SAMPLE001
SAMPLE002
SAMPLE003
DISCOUNT10
SAVE20NOW
WELCOME2024
BULK001,BULK002,BULK003
SPRING2024
SUMMER25
AUTUMN30
WINTER40`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sample_redemption_codes.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading sample file:", error);
      setError("Failed to download sample file");
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Auto-capitalize as user types with length restriction
  const handleCodeChange = (e) => {
    const value = e.target.value;
    
    // Restrict to alphanumeric characters and limit length per code to 20 chars
    const filteredValue = value
      .toUpperCase()
      .replace(/[^A-Z0-9,\s]/g, '') // Only allow letters, numbers, commas, and spaces
      .split(',')
      .map(code => code.trim().substring(0, 20)) // Limit each code to 20 characters
      .join(', ');
    
    setCodes(filteredValue);
    setError(""); // Clear error when user types
    setValidationErrors([]); // Clear validation errors
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative">
        {/* Close Button - Positioned outside the modal */}
        <button
          onClick={handleClose}
          disabled={isLoading}
          className="absolute -top-6 -right-6 w-[45px] h-[45px] cursor-pointer disabled:opacity-50"
        >
          <img src={CloseIcon} alt="Close" className="w-[45px] h-[45px]" />
        </button>

        <div className="bg-white rounded-lg p-6 w-[90vw] max-w-md">
          <h2 className="text-lg font-semibold mb-4">Add Codes</h2>
          
          <div className="mb-2">
            <textarea
              className="w-full h-[120px] border border-[#E5E7EB] rounded-lg p-3 resize-none focus:outline-none focus:border-[#00ADEF] text-[#303030] text-sm disabled:opacity-50"
              placeholder="Type Code (4-20 characters, letters and numbers only)"
              value={codes}
              onChange={handleCodeChange}
              disabled={isLoading}
            />
          </div>
          
          {/* Validation messages below input */}
          {error && (
            <div className="mb-4 text-red-600 text-sm">
              <span>⚠ {error}</span>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="mb-4">
              {validationErrors.map((err, index) => {
                const isSuccess = err.includes('Successfully loaded') || err.includes('loaded');
                const isWarning = err.includes('duplicate') || err.includes('removed') || err.includes('skipped');
                
                return (
                  <div key={index} className={`text-sm ${
                    isSuccess ? 'text-green-600' : 
                    isWarning ? 'text-[#00ADEF]' : 
                    'text-orange-600'
                  }`}>
                    <span>
                      {isSuccess ? '✓ ' : isWarning ? 'ⓘ ' : '⚠ '}
                      {err}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="flex items-center text-[#00ADEF] text-sm mb-4">
            <div className="w-4 h-4 rounded-full border border-[#00ADEF] flex items-center justify-center mr-2 flex-shrink-0">
              <span className="text-xs">i</span>
            </div>
            <span>Enter multiple codes, separated by commas. Codes will be automatically capitalized.</span>
          </div>
          
          <div className="flex items-center my-4">
            <div className="flex-grow h-px bg-gray-300" />
            <span className="mx-3 text-gray-500 text-sm">or</span>
            <div className="flex-grow h-px bg-gray-300" />
          </div>
          
          <div className="flex gap-3 mb-6">
            <button
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7EB] text-sm rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={isLoading}
            >
              <UploadCSVIcon className="w-5 h-5 text-[#00ADEF]" />
              <span className="text-gray-700">Upload CSV File</span>
            </button>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              disabled={isLoading}
            />
            <button
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7EB] text-sm rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              onClick={handleSampleFileDownload}
              disabled={isLoading}
            >
              <SampleUploadIcon className="w-5 h-5 text-green-500" />
              <span className="text-gray-700">Sample File</span>
              <DownloadIcon className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          
          <div className="flex justify-end">
            <button
              className="bg-[#00ADEF] text-white py-2 px-6 font-medium text-sm rounded-lg hover:bg-[#0098d1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleAddCodes}
              disabled={isLoading || !codes.trim()}
            >
              {isLoading ? "Adding..." : "Add Codes"}
            </button>
          </div>

          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
              <div className="flex items-center gap-2 text-[#00ADEF]">
                <div className="w-4 h-4 border-2 border-[#00ADEF] border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Processing...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCodesModal;